/**
 * Formatação de horários de voo preservando o "wall-clock" (horário local do
 * aeroporto), SEM conversão de fuso.
 *
 * Os campos departureTime/arrivalTime vêm da API em formatos variados e cada
 * trecho carrega o horário LOCAL do seu aeroporto:
 *   - "2026-09-09T06:30:00"          (sem offset)
 *   - "2026-07-21T13:40:00-03:00"    (offset do aeroporto de origem)
 *   - "2026-10-21T18:50:00+00:00"    (offset do aeroporto de destino)
 *   - "2026-08-26T10:50:00.000Z"     (Z carimbado pela origem — ainda é local)
 *
 * Em todos os casos o número HH:MM/DD-MM que aparece na string JÁ é o horário
 * local do aeroporto que o passageiro deve ver. Por isso NÃO convertemos fuso:
 * extraímos os componentes literais e formatamos diretamente. Funciona para
 * voos domésticos e internacionais, independente do fuso do navegador.
 *
 * IMPORTANTE: use estas funções APENAS para horários de voo. Para timestamps
 * reais do servidor (ex: createdAt/emittedAt) continue usando new Date().
 */

const WALL_CLOCK_REGEX = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/;

const WEEKDAYS_PT = [
  'Domingo',
  'Segunda-feira',
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado',
];

interface WallClockParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

function parseWallClock(value: string): WallClockParts | null {
  if (!value) return null;
  const m = WALL_CLOCK_REGEX.exec(value.trim());
  if (!m) return null;
  return {
    year: Number(m[1]),
    month: Number(m[2]),
    day: Number(m[3]),
    hour: Number(m[4]),
    minute: Number(m[5]),
  };
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** "HH:MM" literal do aeroporto. Fallback para a string original. */
export function formatFlightTime(value: string): string {
  const p = parseWallClock(value);
  if (!p) return value || '--:--';
  return `${pad2(p.hour)}:${pad2(p.minute)}`;
}

/** "DD/MM/AAAA" literal do aeroporto. Fallback para '' quando não parseia. */
export function formatFlightDate(value: string): string {
  const p = parseWallClock(value);
  if (!p) return '';
  return `${pad2(p.day)}/${pad2(p.month)}/${p.year}`;
}

/** "Dia-da-semana, DD/MM/AAAA" literal do aeroporto. */
export function formatFlightLongDate(value: string): string {
  const p = parseWallClock(value);
  if (!p) return '';
  const weekdayIndex = new Date(
    Date.UTC(p.year, p.month - 1, p.day),
  ).getUTCDay();
  return `${WEEKDAYS_PT[weekdayIndex]}, ${pad2(p.day)}/${pad2(p.month)}/${p.year}`;
}
